import { useState, useEffect, useCallback } from 'react';
import { getBookingCommissions } from '../../lib/bookingCommissions';
import { supabase } from '../../lib/supabase';
import { MapPin, Hand, Shield } from 'lucide-react';

interface BookingProfitLossProps {
  revenue: number;
  materialCosts: number;
  laborCosts: number;
  discounts?: number;
  otherCosts?: number;
  bookingId?: string;
  flatRates?: any[]; // Add flat rates data for detailed breakdown
}

export default function BookingProfitLoss({
  revenue,
  materialCosts,
  laborCosts,
  discounts = 0,
  otherCosts = 0,
  bookingId,
  flatRates = []
}: BookingProfitLossProps) {
  const [commissionCosts, setCommissionCosts] = useState(0);
  const [isLoadingCommissions, setIsLoadingCommissions] = useState(false);
  const [bonusCosts, setBonusCosts] = useState(0);
  const [isLoadingBonuses, setIsLoadingBonuses] = useState(false);
  const [timerLogs, setTimerLogs] = useState<any[]>([]);
  const [isLoadingTimerLogs, setIsLoadingTimerLogs] = useState(false);
  const [timerCosts, setTimerCosts] = useState(0);
  const [productCosts, setProductCosts] = useState(0);
  const [isLoadingProductCosts, setIsLoadingProductCosts] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [expenseCosts, setExpenseCosts] = useState(0);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
  const [expenses, setExpenses] = useState<any[]>([]);

  const loadTimerLogs = useCallback(async () => {
    if (!bookingId || !supabase) return;
    
    try {
      setIsLoadingTimerLogs(true);
      
      const { data: entries, error } = await supabase
        .from('job_time_entries')
        .select('id, resource_id, started_at, stopped_at, duration_seconds, start_method, stop_method')
        .eq('booking_id', bookingId)
        .order('started_at', { ascending: true });

      if (error) {
        console.error('🚨 BookingProfitLoss: Error fetching job time entries:', error);
        setTimerLogs([]);
        setTimerCosts(0);
        return;
      }

      if (!entries || entries.length === 0) {
        setTimerLogs([]);
        setTimerCosts(0);
        return;
      }

      // Group entries by resource, keeping individual sessions
      const byResource: Record<string, typeof entries> = {};
      for (const e of entries) {
        if (e.resource_id) {
          if (!byResource[e.resource_id]) byResource[e.resource_id] = [];
          byResource[e.resource_id].push(e);
        }
      }

      const resourceIds = Object.keys(byResource);
      if (resourceIds.length === 0) {
        setTimerLogs([]);
        setTimerCosts(0);
        return;
      }

      const { data: resources, error: resourceError } = await supabase
        .from('resources')
        .select('id, first_name, last_name, hourly_rate')
        .in('id', resourceIds);

      if (resourceError || !resources?.length) {
        setTimerLogs([]);
        setTimerCosts(0);
        return;
      }

      const enrichedLogs = resources.map((r) => {
        const sessions = byResource[r.id] || [];
        const total_seconds = sessions.reduce((sum, s) => sum + (s.duration_seconds ?? 0), 0);
        const hourlyRate = r.hourly_rate ?? 0;
        const cost = (total_seconds / 3600) * hourlyRate;
        return {
          resource_id: r.id,
          total_seconds,
          sessions,
          resource: { first_name: r.first_name, last_name: r.last_name, hourly_rate: hourlyRate },
          _cost: cost,
        };
      });

      const totalTimerCost = enrichedLogs.reduce((sum, log) => sum + log._cost, 0);

      setTimerLogs(enrichedLogs);
      setTimerCosts(totalTimerCost);
    } catch (error) {
      console.error('🚨 BookingProfitLoss: Error loading job time entries:', error);
      setTimerLogs([]);
      setTimerCosts(0);
    } finally {
      setIsLoadingTimerLogs(false);
    }
  }, [bookingId]);

  // Realtime: re-fetch timer costs whenever job_time_entries change for this booking
  useEffect(() => {
    if (!bookingId) return;

    const channel = supabase
      .channel(`pl-timers-${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_time_entries',
          filter: `booking_id=eq.${bookingId}`,
        },
        () => {
          loadTimerLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [bookingId, loadTimerLogs]);

  // Load commission and bonus costs when component mounts or bookingId changes
  useEffect(() => {
    const loadCommissionCosts = async () => {
      if (!bookingId) return;
      
      try {
        setIsLoadingCommissions(true);
        const commissions = await getBookingCommissions(bookingId);
        const totalCommissions = commissions.reduce((total, commission) => total + commission.commission_amount, 0);
        setCommissionCosts(totalCommissions);
      } catch (error) {
        console.error('Error loading commission costs:', error);
        setCommissionCosts(0);
      } finally {
        setIsLoadingCommissions(false);
      }
    };

    const loadBonusCosts = async () => {
      if (!bookingId || !supabase) return;
      
      try {
        setIsLoadingBonuses(true);
        
        const { data: bonusEntries, error } = await supabase
          .from('payroll_page')
          .select('bonus_earnings, total_earnings')
          .eq('booking_id', bookingId)
          .eq('entry_type', 'bonus');

        if (error) {
          console.error('🚨 BookingProfitLoss: Error fetching bonus entries:', error);
          setBonusCosts(0);
          return;
        }

        const totalBonuses = bonusEntries?.reduce((total, entry) => {
          const bonusAmount = entry.bonus_earnings || entry.total_earnings || 0;
          return total + bonusAmount;
        }, 0) || 0;

        setBonusCosts(totalBonuses);
      } catch (error) {
        console.error('🚨 BookingProfitLoss: Error loading bonus costs:', error);
        setBonusCosts(0);
      } finally {
        setIsLoadingBonuses(false);
      }
    };

    const loadProductCosts = async () => {
      if (!bookingId || !supabase) return;
      
      try {
        setIsLoadingProductCosts(true);
        
        const { data: bookingProducts, error: productsError } = await supabase
          .from('product_booking_sales')
          .select('*')
          .eq('booking_id', bookingId);

        if (productsError) {
          console.error('🚨 BookingProfitLoss: Error fetching booking products:', productsError);
          setProductCosts(0);
          setProducts([]);
          return;
        }

        if (!bookingProducts || bookingProducts.length === 0) {
          setProductCosts(0);
          setProducts([]);
          return;
        }

        const itemIds = bookingProducts.map(p => p.stock_item_id);
        
        const { data: stockItems, error: stockError } = await supabase
          .from('stock_items')
          .select('id, name, acquisition_cost')
          .in('id', itemIds);

        if (stockError) {
          console.error('🚨 BookingProfitLoss: Error fetching stock items:', stockError);
          setProductCosts(0);
          setProducts([]);
          return;
        }

        const enrichedProducts = bookingProducts.map(product => {
          const stockItem = stockItems?.find(item => item.id === product.stock_item_id);
          const acquisitionCost = stockItem?.acquisition_cost || 0;
          const totalCost = acquisitionCost * product.quantity;
          
          return {
            ...product,
            stockItem,
            acquisitionCost,
            totalCost
          };
        });

        const totalProductCost = enrichedProducts.reduce((total, product) => {
          return total + product.totalCost;
        }, 0);

        setProducts(enrichedProducts);
        setProductCosts(totalProductCost);
      } catch (error) {
        console.error('🚨 BookingProfitLoss: Error loading product costs:', error);
        setProductCosts(0);
        setProducts([]);
      } finally {
        setIsLoadingProductCosts(false);
      }
    };

    const loadExpenses = async () => {
      if (!bookingId || !supabase) return;
      
      try {
        setIsLoadingExpenses(true);
        
        const { data: bookingExpenses, error: expensesError } = await supabase
          .from('expenses')
          .select('id, amount, description, merchant_name, expense_date, category_id')
          .eq('booking_id', bookingId);

        if (expensesError) {
          console.error('🚨 BookingProfitLoss: Error fetching expenses:', expensesError);
          setExpenseCosts(0);
          setExpenses([]);
          return;
        }

        if (!bookingExpenses || bookingExpenses.length === 0) {
          setExpenseCosts(0);
          setExpenses([]);
          return;
        }

        const totalExpenseCost = bookingExpenses.reduce((total, expense) => {
          return total + (expense.amount || 0);
        }, 0);

        setExpenses(bookingExpenses);
        setExpenseCosts(totalExpenseCost);
      } catch (error) {
        console.error('🚨 BookingProfitLoss: Error loading expenses:', error);
        setExpenseCosts(0);
        setExpenses([]);
      } finally {
        setIsLoadingExpenses(false);
      }
    };

    loadCommissionCosts();
    loadBonusCosts();
    loadTimerLogs();
    loadProductCosts();
    loadExpenses();
  }, [bookingId, loadTimerLogs]);

  // Calculate resource breakdowns from flat rates
  const resourceBreakdowns = flatRates.reduce((acc: any[], rate) => {
    const resourceId = rate.resource_id || 'unassigned';
    const resourceName = rate.resource_first_name && rate.resource_last_name 
      ? `${rate.resource_first_name} ${rate.resource_last_name}`
      : 'Unassigned';
    
    const rateAmount = rate.resource_flat_rate_amount !== null && rate.resource_flat_rate_amount !== undefined
      ? Number(rate.resource_flat_rate_amount)
      : rate.resource_hourly_rate !== null && rate.resource_hourly_rate !== undefined
      ? Number(rate.resource_hourly_rate)
      : 0;
    
    const earnings = rate.hours * rateAmount;
    
    const existing = acc.find(r => r.resourceId === resourceId);
    if (existing) {
      existing.earnings += earnings;
      existing.hours += rate.hours;
    } else {
      acc.push({
        resourceId,
        resourceName,
        earnings,
        hours: rate.hours,
        rate: rateAmount
      });
    }
    
    return acc;
  }, []);

  const totalLaborCosts = laborCosts + commissionCosts + bonusCosts + timerCosts;
  const totalCosts = materialCosts + totalLaborCosts + productCosts + expenseCosts + otherCosts + discounts;
  const profit = revenue - totalCosts;
  const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
  
  // Format currency to always show 2 decimal places
  const formatCurrency = (amount: number) => {
    return amount.toFixed(2);
  };

  // Format time from seconds to readable format
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatTimeOfDay = (isoString: string | null) => {
    if (!isoString) return null;
    try {
      const d = new Date(isoString);
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return null; }
  };

  const formatDateShort = (isoString: string | null) => {
    if (!isoString) return null;
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch { return null; }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900">Profit & Loss</h3>
      </div>
      
      <div className="p-6 space-y-4">
        {/* Revenue Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">Revenue</span>
            <span className="text-sm font-semibold text-green-600">${formatCurrency(revenue)}</span>
          </div>
        </div>

        {/* Costs Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="mb-3">
            <span className="text-sm font-semibold text-gray-900">Costs</span>
          </div>

          {/* Labor Costs */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Labor Costs</span>
              <span className="text-red-600 font-bold">-${formatCurrency(totalLaborCosts)}</span>
            </div>
            
            {/* Labor Cost Breakdown */}
            {(laborCosts > 0 || commissionCosts > 0 || bonusCosts > 0 || timerCosts > 0 || isLoadingTimerLogs) && (
              <div className="ml-4 space-y-1">
                {/* Individual Resource Breakdowns */}
                {resourceBreakdowns.length > 0 && (
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-gray-600 mb-1">• Flat Rate Times</div>
                    {resourceBreakdowns.map((resource, index) => (
                      <div key={index} className="flex justify-between text-xs text-gray-500 ml-4">
                        <span className="flex items-center">
                          {resource.resourceName}
                          <span className="text-gray-400 ml-1">
                            ({resource.hours}h @ ${resource.rate}/hr)
                          </span>
                        </span>
                        <span>-${formatCurrency(resource.earnings)}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Timer Logs Breakdown */}
                <div className="space-y-1">
                  <div className="text-xs font-medium text-gray-600 mb-1">
                    • Timer Time
                    {isLoadingTimerLogs && ' (loading...)'}
                    {!isLoadingTimerLogs && ` (${timerLogs.length} resource${timerLogs.length !== 1 ? 's' : ''})`}
                  </div>
                  {timerLogs.length === 0 && !isLoadingTimerLogs && (
                    <div className="flex justify-between text-xs text-gray-400 ml-4 italic">
                      <span>No timer logs recorded</span>
                      <span>-$0.00</span>
                    </div>
                  )}
                  {timerLogs.map((log, index) => {
                      const resourceName = log.resource?.first_name && log.resource?.last_name
                        ? `${log.resource.first_name} ${log.resource.last_name}`
                        : 'Unknown Resource';
                      const hourlyRate = log.resource?.hourly_rate || 0;
                      const hours = log.total_seconds / 3600;
                      const cost = hourlyRate * hours;
                      const sessions = log.sessions || [];
                      
                      return (
                        <div key={index} className="ml-4 mb-2">
                          {/* Resource summary row */}
                          <div className="flex justify-between text-xs text-gray-500">
                            <span className="flex items-center font-medium text-gray-600">
                              {resourceName}
                              <span className="text-gray-400 ml-1 font-normal">
                                ({formatTime(log.total_seconds)} @ ${hourlyRate}/hr)
                              </span>
                            </span>
                            <span className="font-medium">-${cost.toFixed(2)}</span>
                          </div>
                          {/* Individual sessions */}
                          {sessions.length > 0 && (
                            <div className="ml-3 mt-1 space-y-0.5">
                              {sessions.map((session: any, sIdx: number) => {
                                const method = session.start_method || 'manual';
                                const isGps = method === 'auto_gps';
                                const isAdmin = method === 'admin';
                                const arrival = formatTimeOfDay(session.started_at);
                                const departure = formatTimeOfDay(session.stopped_at);
                                const dateStr = formatDateShort(session.started_at);
                                const dur = session.duration_seconds ?? 0;
                                const isRunning = !session.stopped_at;
                                
                                return (
                                  <div key={session.id || sIdx} className="flex items-center gap-1.5 text-[11px] text-gray-400">
                                    {/* Method badge */}
                                    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                      isGps
                                        ? 'bg-green-50 text-green-600'
                                        : isAdmin
                                        ? 'bg-orange-50 text-orange-600'
                                        : 'bg-blue-50 text-blue-600'
                                    }`}>
                                      {isGps ? <MapPin className="h-2.5 w-2.5" /> : isAdmin ? <Shield className="h-2.5 w-2.5" /> : <Hand className="h-2.5 w-2.5" />}
                                      {isGps ? 'GPS' : isAdmin ? 'Admin' : 'Manual'}
                                    </span>
                                    {/* Arrival / Departure */}
                                    <span className="text-gray-500">
                                      {dateStr && <span>{dateStr} · </span>}
                                      {arrival || '—'}
                                      <span className="mx-0.5">→</span>
                                      {isRunning ? (
                                        <span className="text-green-600 font-medium">Active</span>
                                      ) : (
                                        departure || '—'
                                      )}
                                    </span>
                                    {dur > 0 && (
                                      <span className="text-gray-400">({formatTime(dur)})</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
                
                {/* Show total if no breakdown available */}
                {resourceBreakdowns.length === 0 && laborCosts > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>• Hourly/Flat Rates</span>
                    <span>-${formatCurrency(laborCosts)}</span>
                  </div>
                )}
                
                {commissionCosts > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>• Commissions{isLoadingCommissions ? ' (loading...)' : ''}</span>
                    <span>-${formatCurrency(commissionCosts)}</span>
                  </div>
                )}
                {bonusCosts > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>• Bonuses{isLoadingBonuses ? ' (loading...)' : ''}</span>
                    <span>-${formatCurrency(bonusCosts)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Material Costs */}
          {materialCosts > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Material Costs</span>
              <span className="text-red-600 font-bold">-${formatCurrency(materialCosts)}</span>
            </div>
          )}

          {/* Product Costs */}
          {(productCosts > 0 || isLoadingProductCosts) && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">
                  Product Costs
                  {isLoadingProductCosts && ' (loading...)'}
                </span>
                <span className="text-red-600 font-bold">-${formatCurrency(productCosts)}</span>
              </div>
              
              {/* Product Cost Breakdown */}
              {products.length > 0 && (
                <div className="ml-4 space-y-1">
                  {products.map((product, index) => (
                    <div key={index} className="flex justify-between text-xs text-gray-500">
                      <span className="flex items-center">
                        • {product.product_name || product.stockItem?.name || 'Unknown Product'}
                        <span className="text-gray-400 ml-1">
                          ({product.quantity} × ${formatCurrency(product.acquisitionCost)})
                        </span>
                      </span>
                      <span>-${formatCurrency(product.totalCost)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Discounts */}
          {discounts > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Discounts</span>
              <span className="text-red-600 font-bold">-${formatCurrency(discounts)}</span>
            </div>
          )}

          {/* Expenses */}
          {(expenseCosts > 0 || isLoadingExpenses) && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-medium">
                  Expenses
                  {isLoadingExpenses && ' (loading...)'}
                </span>
                <span className="text-red-600 font-bold">-${formatCurrency(expenseCosts)}</span>
              </div>
              
              {/* Expense Breakdown */}
              {expenses.length > 0 && (
                <div className="ml-4 space-y-1">
                  {expenses.map((expense, index) => (
                    <div key={expense.id || index} className="flex justify-between text-xs text-gray-500">
                      <span className="flex items-center">
                        • {expense.description || expense.merchant_name || 'Expense'}
                        {expense.merchant_name && expense.description && (
                          <span className="text-gray-400 ml-1">
                            ({expense.merchant_name})
                          </span>
                        )}
                      </span>
                      <span>-${formatCurrency(expense.amount || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Other Costs */}
          {otherCosts > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 font-medium">Other Costs</span>
              <span className="text-red-600 font-bold">-${formatCurrency(otherCosts)}</span>
            </div>
          )}

          {/* Total Costs */}
          <div className="flex justify-between text-sm pt-3 border-t border-gray-200">
            <span className="text-gray-700 font-semibold">Total Costs</span>
            <span className="text-red-600 font-semibold">-${formatCurrency(totalCosts)}</span>
          </div>
        </div>

        {/* Net Profit Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-900">Net Profit</div>
              <div className="text-xs text-gray-500">
                {profitMargin.toFixed(1)}% margin
              </div>
            </div>
            <div className="text-right">
              <div className={`text-sm font-semibold ${
                profit >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {profit >= 0 ? '+' : '-'}${formatCurrency(Math.abs(profit))}
              </div>
              <div className="text-xs text-gray-500">
                {profit >= 0 ? 'Profit' : 'Loss'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}